import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import { documentRepo } from '../../data/db';
import type { Document } from '../../types';
import type { FitMode } from './DocumentViewerToolbar';
import DocumentViewerToolbar from './DocumentViewerToolbar';
import DocumentViewerCanvas from './DocumentViewerCanvas';
import DocumentViewerSidebar from './DocumentViewerSidebar';
import { extractTextFromPdf } from '../../utils/pdfText';

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;

export interface DocumentViewerProps {
  document: Document;
  /** Override back link (e.g. for Legal Forms). Default: /dashboard/documents */
  backHref?: string;
  /** Override edit link; pass null to hide edit. Default: /dashboard/documents/:id/edit */
  editHref?: string | null;
  /** Override delete; pass null to hide delete. Default: show delete and modal */
  onDelete?: (() => void) | null;
  /** Override download (e.g. for Legal Forms API download). Default: use doc.url */
  onDownload?: () => void;
  /** Hide the download button entirely (e.g. for Legal Forms / Forms & Precedents) */
  hideDownload?: boolean;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document: doc,
  backHref: backHrefOverride,
  editHref: editHrefOverride,
  onDelete: onDeleteOverride,
  onDownload: onDownloadOverride,
  hideDownload,
}) => {
  const navigate = useNavigate();
  const [zoom, setZoom] = useState(1);
  const [fitMode, setFitMode] = useState<FitMode>('width');
  const [rotation, setRotation] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [documentText, setDocumentText] = useState<string | null>(null);
  const [documentTextLoading, setDocumentTextLoading] = useState(false);

  const isPdf = doc.url && (doc.name.toLowerCase().endsWith('.pdf') || doc.url.toLowerCase().includes('.pdf'));
  const pageCount = isPdf ? 16 : 1; // Demo: assume multi-page PDF; in production get from API/PDF.js

  // Extract text from PDF for the chat companion
  useEffect(() => {
    if (!isPdf || !doc.url) {
      setDocumentText(null);
      return;
    }
    let cancelled = false;
    setDocumentTextLoading(true);
    setDocumentText(null);
    extractTextFromPdf(doc.url)
      .then((text) => {
        if (!cancelled) {
          setDocumentText(text || null);
        }
      })
      .catch(() => {
        if (!cancelled) setDocumentText(null);
      })
      .finally(() => {
        if (!cancelled) setDocumentTextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPdf, doc.url]);

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP));
    setFitMode('custom');
  }, []);
  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP));
    setFitMode('custom');
  }, []);
  const handleFitWidth = useCallback(() => {
    setFitMode('width');
    setZoom(1);
  }, []);
  const handleFitPage = useCallback(() => {
    setFitMode('page');
    setZoom(1);
  }, []);
  const handleRotate = useCallback(() => {
    setRotation((r) => (r + 90) % 360);
  }, []);
  const handleDownloadDefault = useCallback(() => {
    if (doc.url) {
      const a = document.createElement('a');
      a.href = doc.url;
      a.download = doc.name || 'document';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    }
  }, [doc.url, doc.name]);
  const handleDownload = onDownloadOverride ?? handleDownloadDefault;
  const handlePrint = useCallback(() => {
    if (doc.url) window.open(doc.url, '_blank', 'noopener')?.print();
    else window.print();
  }, [doc.url]);
  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen((v) => !v);
  }, []);
  const handlePrevPage = useCallback(() => {
    setCurrentPage((p) => Math.max(1, p - 1));
  }, []);
  const handleNextPage = useCallback(() => {
    setCurrentPage((p) => Math.min(pageCount, p + 1));
  }, [pageCount]);

  const handleDelete = useCallback(() => {
    documentRepo.delete(doc.id);
    setShowDeleteModal(false);
    navigate('/dashboard/documents');
  }, [doc.id, navigate]);

  const backHref = backHrefOverride ?? '/dashboard/documents';
  const editHref = editHrefOverride !== undefined ? editHrefOverride : `/dashboard/documents/${doc.id}/edit`;
  const showDelete = onDeleteOverride !== undefined ? onDeleteOverride != null : true;
  const handleDeleteClick = onDeleteOverride !== undefined ? onDeleteOverride : (() => setShowDeleteModal(true));

  const docType = useMemo(() => {
    if (!doc.url) return 'other';
    const n = (doc.name || '').toLowerCase();
    if (n.endsWith('.pdf')) return 'pdf';
    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(n)) return 'image';
    if (n.endsWith('.docx')) return 'docx';
    return 'other';
  }, [doc.url, doc.name]);

  return (
    <>
      <div
        className={`flex flex-col bg-white dark:bg-slate-800 rounded-2xl overflow-hidden ${
          isFullscreen ? 'fixed inset-0 z-[1000] h-screen min-h-screen rounded-none' : 'h-[calc(100vh-8rem)] min-h-[calc(100vh-8rem)]'
        }`}
      >
        <DocumentViewerToolbar
          onDownload={handleDownload}
          onPrint={handlePrint}
          // DOCX doesn't have supported in-view searching yet.
          onSearch={isPdf ? () => setSearchActive((s) => !s) : undefined}
          searchActive={searchActive}
          isFullscreen={isFullscreen}
          onToggleFullscreen={handleToggleFullscreen}
          documentName={doc.name}
          backHref={backHref}
          editHref={editHref ?? undefined}
          onDelete={showDelete ? (handleDeleteClick ?? undefined) : undefined}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((v) => !v)}
          hideDownload={hideDownload}
        />
        <div className="flex flex-1 min-h-0 overflow-hidden h-full">
          <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden h-full">
            <DocumentViewerCanvas
              url={doc.url}
              type={docType}
              zoom={zoom}
              rotation={rotation}
              fitMode={fitMode}
              currentPage={currentPage}
              onDownload={handleDownload}
              documentName={doc.name}
              hideDownload={hideDownload}
            />
          </div>
          <DocumentViewerSidebar
            document={doc}
            documentContent={documentText}
            documentContentLoading={documentTextLoading}
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen((v) => !v)}
          />
        </div>
      </div>
      {showDelete && (
        <ConfirmDeleteModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title="Delete document"
          message="Are you sure you want to delete this document? This cannot be undone."
          confirmLabel="Delete"
        />
      )}
    </>
  );
};

export default DocumentViewer;
